import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/auth.service';
import pushTokenService from '../services/pushToken.service';
import { loadStoredTokenInfo, clearStoredTokenInfo } from '../utils/fcmTokenStorage';
import cacheService from '../utils/cache.service';
import { ensureFreshAccessToken, onAccessTokenRefreshed } from '../utils/accessTokenRefresh';
import { clearNativeAuth, syncNativeAuth } from '../utils/nativeShell';
import { User, LoginData, RegisterData, AuthContextType } from '../types/auth.types';
import storeService from '../services/store.service';
import { getSupportAccess } from '../utils/superAdminSupportAccess';
import { isOAuthPopup, isOAuthReturnLocation } from '../utils/oauthCallback';

/**
 * Authentication Context
 * Manages global authentication state
 */

const AUTH_ERRORS: Record<string, string> = {
  'Invalid email or password': 'E-mail ou mot de passe incorrect',
  'Login failed': 'Connexion impossible',
  'Registration failed': 'Inscription impossible',
};

function authErrorMessage(raw: string): string {
  return AUTH_ERRORS[raw] || raw;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      if (isOAuthPopup() && isOAuthReturnLocation()) {
        setIsLoading(false);
        return;
      }

      if (authService.isSessionExpiredByDate()) {
        authService.clearAuthData();
        clearNativeAuth();
        setIsLoading(false);
        return;
      }

      const tokens = authService.getTokens();
      const savedUser = authService.getUser();

      if (!tokens.refreshToken || !savedUser) {
        setIsLoading(false);
        return;
      }

      const fresh = await ensureFreshAccessToken();
      if (!fresh) {
        authService.clearAuthData();
        clearNativeAuth();
        setIsLoading(false);
        return;
      }

      setAccessToken(fresh);
      setRefreshToken(tokens.refreshToken);

      let nextUser = savedUser;
      const simulatedId = getSupportAccess()?.simulatedUser?.id;
      if (simulatedId) {
        try {
          const current = await authService.getCurrentUser();
          if (current?.user && Number(current.user.id) === Number(simulatedId)) {
            nextUser = current.user;
            authService.saveUser(nextUser);
          }
        } catch {
          // On garde le profil déjà enregistré si le rechargement échoue.
        }
      }

      setUser(nextUser);
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => onAccessTokenRefreshed((token) => {
    setAccessToken(token);
    const refresh = localStorage.getItem('refreshToken');
    const savedUser = authService.getUser();
    const storeId = storeService.getCurrentStore()?.id ?? null;
    syncNativeAuth(token, refresh, savedUser?.id ?? null, storeId);
  }), []);

  useEffect(() => {
    if (!user || !accessToken) return;
    const storeId = storeService.getCurrentStore()?.id ?? null;
    syncNativeAuth(accessToken, refreshToken, user.id, storeId);
  }, [user?.id, accessToken, refreshToken]);

  /**
   * Register a new user
   * @param data - Registration data
   */
  const register = async (data: RegisterData): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authService.register(data);

      if (response.success && response.data) {
        const { user, accessToken, refreshToken } = response.data;

        // Save to state
        setUser(user);
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);

        // Save to localStorage
        authService.saveTokens(accessToken, refreshToken);
        authService.saveUser(user);
        authService.saveLoginDate();
        syncNativeAuth(accessToken, refreshToken, user.id, storeService.getCurrentStore()?.id ?? null);
      } else {
        throw new Error(response.error?.message || 'Inscription impossible');
      }
    } catch (err: any) {
      const errorMessage = authErrorMessage(
        err.response?.data?.error?.message || err.message || 'Inscription impossible'
      );
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login a user
   * @param data - Login credentials
   */
  const login = async (data: LoginData): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const storeId = storeService.getCurrentStore()?.id;
      const response = await authService.login(storeId ? { ...data, storeId } : data);

      if (response.success && response.data) {
        const { user, accessToken, refreshToken } = response.data;

        // If a different user was previously active on this device, deactivate their FCM token
        // using the fresh access token so the authenticated call succeeds.
        const previousUserRaw = localStorage.getItem('user');
        const previousUserId = previousUserRaw ? JSON.parse(previousUserRaw)?.id : null;
        if (previousUserId && previousUserId !== user.id) {
          const oldTokenInfo = loadStoredTokenInfo(previousUserId);
          if (oldTokenInfo?.token) {
            pushTokenService.deactivateToken(oldTokenInfo.token).catch(() => {});
            clearStoredTokenInfo(previousUserId);
          }
        }

        // Save to state
        setUser(user);
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);

        // Save to localStorage
        authService.saveTokens(accessToken, refreshToken);
        authService.saveUser(user);
        authService.saveLoginDate();
        syncNativeAuth(accessToken, refreshToken, user.id, storeService.getCurrentStore()?.id ?? null);
      } else {
        throw new Error(response.error?.message || 'Connexion impossible');
      }
    } catch (err: any) {
      const errorMessage = authErrorMessage(
        err.response?.data?.error?.message || err.message || 'Connexion impossible'
      );
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   * Deactivates FCM token for this device *before* clearing auth so the API call is authenticated.
   */
  const logout = async (): Promise<void> => {
    const userId = user?.id ?? null;

    if (userId) {
      cacheService.clearPermissionsForUser(userId);
      cacheService.clearDashboardLayoutForAllStores();
      cacheService.clearAll();
    }

    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setError(null);
    clearNativeAuth();
    authService.clearAuthData();
    authService.logout().catch(() => {});
  };

  /**
   * Refresh access token
   */
  const refreshAccessToken = async (): Promise<void> => {
    try {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const { accessToken: newAccessToken } = await authService.refreshToken(refreshToken);

      setAccessToken(newAccessToken);
      localStorage.setItem('accessToken', newAccessToken);
    } catch (err) {
      // Refresh failed, logout user
      logout();
      throw err;
    }
  };

  /**
   * Clear error message
   */
  const clearError = (): void => {
    setError(null);
  };

  /**
   * Update user data (after profile update)
   */
  const updateUser = async (): Promise<void> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
      authService.saveUser(userData.user);
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const value: AuthContextType = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    error,
    login,
    register,
    logout,
    refreshAccessToken,
    clearError,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 * @returns AuthContextType
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

